/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-incorrect-calls-with-inline-anonymous-functions */
/* eslint-disable ember/no-runloop */

import { action, computed } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import HotelStrategyStrategyDetailWithTabsBaseController from 'lumen/controllers/hotel/strategy/strategy-detail-with-tabs-base';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  COLUMN_PROPERTIES,
  SUBTYPE_VALUES_KEY_PATTERN,
  TYPE_ID_ROOMTYPE,
} from 'lumen/utils/constants/rate-strategy-constants';
import { formatTableRate } from 'lumen/utils/strategy/detail-view-helpers';
import { timeout } from 'lumen/utils/waiters';

const TAB_HEADER_HEIGHT = 56;

const ROOMTYPE_SELECTION_HEADER_HEIGHT = 41;

export default class HotelStrategyRoomtypeController extends HotelStrategyStrategyDetailWithTabsBaseController {
  queryParams = ['sortBy', 'sortDirection', 'tab', 'roomtype'];

  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service simpleExport;

  @tracked roomtype = DEFAULT_QP_VALUES.hotel.strategy.roomtype.roomtype;

  typeId = TYPE_ID_ROOMTYPE;

  exportRSTableTask = task({ drop: true }, async () => {
    const sheetName =
      this.typesDataPerId[this.typeId].formattedTypeName.toString();

    const headers = [this.intl.t('general.hotels').toString()];

    this.subtypes.forEach((subtype) => {
      // Add a label between brackets since grouped columns are not possible in the simple exports
      const subtypeLabel = subtype.formattedLabel[SUBTYPE_VALUES_KEY_PATTERN];
      headers.push(`${this.intl.t('rates.rate')} (${subtypeLabel})`);
      headers.push(`${this.intl.t('general.room_name')} (${subtypeLabel})`);
    });

    const content = [];
    this.roomtypeDataPerHotelInfo.forEach((rowObject) => {
      const row = [rowObject[COLUMN_PROPERTIES.HOTELINFO].formattedValue];

      this.subtypes.forEach((subtype) => {
        row.push(
          formatTableRate(rowObject[subtype.id][COLUMN_PROPERTIES.RATE]),
        );
        row.push(
          rowObject[subtype.id][COLUMN_PROPERTIES.ROOM_NAME].formattedValue,
        );
      });

      content.push(row);

      // Add subrows
      for (let i = 0; i < rowObject.numOfSubRows; i++) {
        const subrow = [rowObject[COLUMN_PROPERTIES.HOTELINFO].formattedValue];

        this.subtypes.forEach((subtype) => {
          if (i < rowObject[subtype.id].subpatterns.length) {
            subrow.push(
              formatTableRate(
                rowObject[subtype.id].subpatterns[i][COLUMN_PROPERTIES.RATE],
              ),
            );
            subrow.push(
              rowObject[subtype.id].subpatterns[i][
                COLUMN_PROPERTIES.ROOM_NAME
              ].formattedValue.toString(),
            );
          } else {
            // Add dummy cells for Price and Roomname
            subrow.push('');
            subrow.push('');
          }
        });

        content.push(subrow);
      }
    });

    const postData = JSON.stringify({ headers, content, sheetName });
    this.simpleExport.execute(postData, this.hotelAndMarketService.hotel?.id);

    // Add a cooldown before the export can be triggered again
    // Prevents multiple downloads on double click or worse: Chrome disabling downloads from mylighthouse.com
    await timeout();
  });

  @computed('hasRSData', 'selectedCompetitors', 'subtypes', 'typesDataPerId')
  get roomtypeDataPerHotelInfo() {
    const roomtypeDataPerHotelInfo = new Map();

    if (this.hasRSData) {
      this.selectedCompetitors.forEach((hotelinfo) => {
        const tableColumns = {
          [COLUMN_PROPERTIES.HOTELINFO]: {
            id: hotelinfo.id,
            formattedValue: hotelinfo.name,
            isOwnHotelInfo:
              this.hotelAndMarketService.isOwnHotelInfo(hotelinfo),
            isExpandable: true,
            isExpanded: false,
          },
          numOfSubRows: 0, // The number of possible subrows per hotelinfo (used to determine if expand icon should be shown)
        };

        // Add a dummy object for each subtype (corresponds to a grouped column in the detail view)
        this.subtypes.forEach((subtype) => {
          tableColumns[subtype.id] = {
            subpatterns: [],
          };
        });

        roomtypeDataPerHotelInfo.set(hotelinfo.id, tableColumns);
      });

      this.subtypes.forEach((subtype) => {
        subtype.filteredPatterns.forEach((pattern) => {
          roomtypeDataPerHotelInfo.get(pattern.hotelinfo.id)[subtype.id][
            COLUMN_PROPERTIES.RATE
          ] = {
            value: pattern.rate,
            formattedValue: pattern.formattedRate,
            baserate: pattern.rsbaserate,
            hasNoData: pattern.hasNoData,
            subsortOrder: pattern.subsortOrder,
          };

          roomtypeDataPerHotelInfo.get(pattern.hotelinfo.id)[subtype.id][
            COLUMN_PROPERTIES.ROOM_NAME
          ] = {
            value: pattern.formattedRoomname,
            formattedValue: pattern.formattedRoomname,
            hasNoData: pattern.rsbaserate?.roomname === undefined,
            subsortOrder: pattern.subsortOrder,
          };

          roomtypeDataPerHotelInfo.get(pattern.hotelinfo.id).numOfSubRows =
            Math.max(
              roomtypeDataPerHotelInfo.get(pattern.hotelinfo.id).numOfSubRows,
              pattern.rssubpatterns.length,
            );

          pattern.rssubpatterns.forEach((subpattern) => {
            roomtypeDataPerHotelInfo
              .get(pattern.hotelinfo.id)
              [subtype.id].subpatterns.push({
                [COLUMN_PROPERTIES.RATE]: {
                  value: subpattern.rate,
                  formattedValue: subpattern.formattedRate,
                  baserate: subpattern.rsbaserate,
                  hasNoData: subpattern.hasNoData,
                  subsortOrder: subpattern.subsortOrder,
                },
                [COLUMN_PROPERTIES.ROOM_NAME]: {
                  value: subpattern.formattedRoomname,
                  formattedValue: subpattern.formattedRoomname,
                  hasNoData: subpattern.hasNoData,
                  subsortOrder: subpattern.subsortOrder,
                },
              });
          });
        });
      });
    }

    return roomtypeDataPerHotelInfo;
  }

  @action
  checkDefaultSelectedRoomtype() {
    // Do extra check to see if hotel has any data for the default selected roomtype (e.g. we've seen cases of hotels with only custom roomtypes)
    const hasStandardRoomtype = this.subtypes.filter(
      (subtype) =>
        subtype.id === DEFAULT_QP_VALUES.hotel.strategy.roomtype.roomtype,
    ).length;

    if (!hasStandardRoomtype) {
      this.roomtype = this.subtypes[0].id;
    }
  }

  // Overriding this from the base class because the roomtype detail model has an extra header (ROOMTYPE_SELECTION_HEADER_HEIGHT)
  @action
  resetContainerDimensions() {
    this.isModalInitialized = true;
    scheduleOnce('afterRender', this, () => {
      this.height =
        document.querySelector('.modal--stretched')?.offsetHeight -
        TAB_HEADER_HEIGHT -
        ROOMTYPE_SELECTION_HEADER_HEIGHT;
      this.width = document.querySelector('.modal--stretched')?.offsetWidth;
    });
  }

  @action
  updateSelectedRoomType(selectedRoomtypeId) {
    this.roomtype = selectedRoomtypeId;
  }
}
